/**
 * Oversikt → Dagen (manager redesign, 2026-10-09): who is working and what needs a look today, as a
 * list. No calendar here; the planning timeline is Vaktplan's day view ("Åpne dagsplan"). Planned
 * time and recorded time are separate columns; presence and review are separate (day.ts).
 */
import { errorText } from '@/api/client';
import {
  type ApiShift,
  useAbsences,
  useAvailabilities,
  useShiftTypes,
  useShiftWrites,
  useTaskStatus,
  useTeams,
  useUsers,
  useWorkTimeChanges,
} from '@/api/data';
import { useLabour } from '@/api/labour';
import { invalidateShifts } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { breakPolicy, dayEntries, hours, oslo, osloISO, rulePerson, tenantRules } from '@/rules';
import { modeOf } from '@/rules/breakSetting';
import { ratesOf } from '@/rules/labour';
import { type NeedProfile, needAt } from '@/shell/local';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Empty, Skeleton, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { PersonLabel } from '@/ui/PersonLabel';
import { DNL, avTone, fmtDs, fullName, hhmm, num, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { DetailPanel, Explain, Fact, Status, initialsOf } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { addDays, dow } from '@timefly/core';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { events, clock, csvOf, estimate, perHour, span, unscheduled } from '../dagen/today';
import { taskDue } from '../hjem/cards';
import { FirstPlanHint } from '../vaktplan/FirstPlan';
import { type DraftShift, ShiftDrawer, draftOf } from '../vaktplan/ShiftDrawer';
import { toVaktplan } from '../vaktplan/link';
import { useShiftLock } from '../vaktplan/lock';
import { availOn } from '../vaktplan/model';
import { gapText, hourGaps } from '../vaktplan/staffing';
import { ReadNotice } from './ReadNotice';
import { type DayRow, FILTERS, type Filter, counts, freeShown, matches, showsPause, tasksLeft } from './day';

const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');
const MON = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
const dayTitle = (d: string) =>
  `${(DNL[dow(d)] ?? '').toLowerCase()} ${Number(d.slice(8, 10))}. ${MON[Number(d.slice(5, 7)) - 1]}`;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? T(one) : T(many)}`;

export function DagenTab({
  d,
  today,
  now,
  rows,
  rota,
  shiftsQ,
  dayShifts,
}: {
  d: string;
  today: string;
  now: number;
  rows: DayRow[];
  rota: boolean;
  shiftsQ: UseQueryResult<{ shifts: ApiShift[] }>;
  dayShifts: ApiShift[];
}) {
  const can = useCan();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const { tenant } = useTenant();
  // a shift keeps the mode it was punched in under; before the punch the company's applies
  const breakMode = modeOf(tenant);
  const labour = useLabour();
  const usersQ = useUsers();
  const teamsQ = useTeams();
  const absQ = useAbsences();
  const availQ = useAvailabilities();
  const typesQ = useShiftTypes();
  const w = useShiftWrites();
  const lock = useShiftLock(async () => {
    const r = await shiftsQ.refetch();
    if (r.status === 'error') throw r.error;
    return r.data ?? null;
  });
  const taskStatus = useTaskStatus();
  const seesHistory = can('timeliste.se');
  const historyQ = useWorkTimeChanges(
    { from: osloISO(d, '00:00'), to: osloISO(addDays(d, 1), '00:00') },
    seesHistory,
  );
  const [draft, setDraft] = useState<DraftShift | null>(null);
  // cost is shown by default to someone who may see it (#94 point 7); the toggle hides it
  const [showCost, setShowCost] = useState(true);
  const [freeQ, setFreeQ] = useState('');
  const [freeAll, setFreeAll] = useState(false);
  const [laterOpen, setLaterOpen] = useState(false);
  const [doneTask, setDoneTask] = useState<number | null>(null);

  const users = usersQ.data ?? [];
  const types = typesQ.data ?? [];
  const team = can('vaktplan.se') ? Number(sp.get('lag')) || 0 : 0;
  const listKnown = shiftsQ.data !== undefined && (!team || usersQ.data !== undefined);
  const staffingKnown = shiftsQ.data !== undefined && labour.settings !== undefined;
  const staffingReady = staffingKnown && !shiftsQ.isError && !labour.query.isError;
  const candidatesReady =
    staffingReady &&
    can('fravaer.se') &&
    [usersQ, absQ, availQ].every((q) => q.data !== undefined && !q.isError);
  const filter = (FILTERS.some(([k]) => k === sp.get('vis')) ? sp.get('vis') : 'alle') as Filter;
  const selectedId = Number(sp.get('valgt')) || 0;
  const set = (k: string, v: string | null, replace = true) => {
    const next = new URLSearchParams(sp);
    if (v == null || v === '') next.delete(k);
    else next.set(k, v);
    setSp(next, { replace });
  };
  const go = (day: string) => {
    const next = new URLSearchParams(sp);
    next.delete('valgt');
    if (day === today) next.delete('d');
    else next.set('d', day);
    setSp(next);
  };

  // a person in several teams belongs to each of them; an open shift belongs to no team
  const teamOf = new Map(users.map((u) => [u.id, (u.teams ?? []).map((t) => t.id)]));
  const inTeam = (r: DayRow) =>
    !team || (r.shift.user_id != null && (teamOf.get(r.shift.user_id) ?? []).includes(team));
  const scoped = rows.filter(inTeam);
  const shown = scoped.filter((r) => matches(r, filter));
  const c = counts(scoped);
  const selected = selectedId ? rows.find((r) => r.shift.id === selectedId) : undefined;
  const teamName = (id: number) => (teamsQ.data ?? []).find((t) => t.id === id)?.name;
  const userTeams = (id: number | null) =>
    id == null
      ? ''
      : (users.find((u) => u.id === id)?.teams ?? [])
          .map((t) => t.name)
          .filter(Boolean)
          .join(', ');

  // the plan's totals for the rows in scope: planned shifts only, a punch with no plan has no planned hours
  const planned = scoped.filter((r) => r.shift.user_id != null && !r.unplanned).map((r) => r.shift);
  const est = estimate(planned, ratesOf(labour.rates), breakPolicy(tenant ?? undefined));
  const plannedPeople = new Set(planned.map((s) => s.user_id)).size;
  const seesCost = can('vaktplan.kost');

  const dayAll = dayShifts.filter((s) => oslo(s.start_time)?.date === d);
  const gaps = rota ? hourGaps(labour.need, d, dayAll) : [];
  const hasNeed = Object.keys(labour.need?.[dow(d)] ?? {}).length > 0;
  const free =
    rota && candidatesReady ? unscheduled(users, dayAll, absQ.data ?? [], availQ.data ?? [], d) : [];
  const freeList = freeShown(free, freeQ, freeAll);

  const tasks = dayAll
    .flatMap((sh) =>
      (sh.taskAssignments ?? [])
        .filter((a) => a.status !== 'completed' && a.id !== doneTask)
        .map((a) => ({ a, sh, due: taskDue(a, sh), start: taskStart(a, sh) })),
    )
    .sort((x, y) => x.start - y.start || x.due - y.due);
  const nowTasks = tasks.filter((t) => t.start <= now || t.due < now);
  const laterTasks = tasks.filter((t) => !(t.start <= now || t.due < now));

  const tomorrow = addDays(d, 1);
  const ahead = (absQ.data ?? [])
    .filter((a) => a.status === 'accepted' && (oslo(a.end_date)?.date ?? a.end_date) >= d)
    .filter((a) => (oslo(a.start_date)?.date ?? a.start_date) <= addDays(d, 13))
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const hendelser = events(Array.isArray(historyQ.data) ? historyQ.data : [], dayShifts, users);
  const updated = shiftsQ.dataUpdatedAt ? clock(new Date(shiftsQ.dataUpdatedAt).toISOString()) : null;

  const plannedText = (s: ApiShift, r: DayRow) =>
    r.unplanned || !rota ? null : `${clock(s.start_time)}–${clock(s.end_time)}`;
  const actualText = (s: ApiShift) =>
    s.punch_in_time
      ? `${clock(s.punch_in_time)}–${s.punch_out_time ? clock(s.punch_out_time) : T('åpen')}`
      : null;
  // the shift type ("Dag", "Kveld"), the old Dagen's Vakt column; a punch with no plan has none
  const vaktText = (r: DayRow) => (r.unplanned || !rota ? '' : typeName(r.shift.shiftType, ''));
  const openRow = (r: DayRow) => set('valgt', String(r.shift.id), false);
  const close = () => set('valgt', null);

  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob(
        [
          csvOf([
            [
              T('Ansatt'),
              T('Vakt'),
              T('Planlagt'),
              T('Registrert'),
              T('Registrert pause (min)'),
              T('Status'),
            ],
            ...shown.map((r) => [
              r.shift.user_id == null ? T('Ledig vakt') : fullName(r.shift.user),
              vaktText(r),
              plannedText(r.shift, r) ?? '',
              actualText(r.shift) ?? '',
              r.pauseMin == null ? '' : String(r.pauseMin),
              r.label,
            ]),
          ]),
        ],
        { type: 'text/csv;charset=utf-8' },
      ),
    );
    a.download = `dagen-${d}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const complete = (id: number) => {
    setDoneTask(id);
    taskStatus
      .mutateAsync({ id, status: 'completed' })
      .then(() => {
        toast(T('Aktiviteten er fullført'));
        return invalidateShifts(qc);
      })
      .catch((e: Error) => {
        setDoneTask(null);
        toast(errorText(e));
      });
  };

  const drawerShift = draft?.id != null ? dayShifts.find((x) => x.id === draft.id) : undefined;
  const drawerUser = users.find((u) => u.id === drawerShift?.user_id);
  // correcting a punch is the planner's editor with the punch fields; the server checks each field against the role
  const mayCorrect = can('vaktplan.endre') || can('timeliste.rette');
  const firstGap = gaps[0];

  const list = (
    <section className="mcard" aria-labelledby="ov-team">
      <div className="mch">
        <div>
          <h2 id="ov-team" tabIndex={-1}>
            {T('Dagens team')}
          </h2>
          <div className="sub oxcount" data-testid="ov-counts">
            <span>
              {!listKnown ? '–' : plural(c.present, 'person på jobb', 'personer på jobb')}
              {c.onBreak > 0 && ` · ${c.onBreak} ${T('på pause')}`}
            </span>
            {rota && listKnown && (
              <span data-testid="ov-later">
                {c.later > 0
                  ? `${plural(c.later, 'kommer senere', 'kommer senere')} · ${T('neste')} ${c.next}`
                  : T('ingen flere i dag')}
              </span>
            )}
            {c.review > 0 && (
              <b className="oxwarn">{plural(c.review, 'vakt å avklare', 'vakter å avklare')}</b>
            )}
          </div>
        </div>
        <span className="sp" />
        {can('vaktplan.se') && (
          <label className="mf oxteam">
            <span>{T('Team i listen')}</span>
            <select className="inp" value={team || ''} onChange={(e) => set('lag', e.target.value || null)}>
              <option value="">{T('Alle team')}</option>
              {team && !teamName(team) ? (
                <option value={team}>
                  {T('Valgt team')} #{team}
                </option>
              ) : null}
              {(teamsQ.data ?? [])
                .filter((t) => t.name)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </label>
        )}
        <label className="oxf">
          <span>{T('Oppmøte')}</span>
          <select
            className="inp"
            value={filter}
            onChange={(e) => set('vis', e.target.value === 'alle' ? null : e.target.value)}
            aria-label={T('Vis oppmøte')}
            data-testid="ov-filter"
          >
            {FILTERS.filter(([k]) => rota || (k !== 'later' && k !== 'open')).map(([k, label]) => (
              <option key={k} value={k}>
                {T(label)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ReadNotice
        reads={[
          [T('dagens vakter'), shiftsQ],
          ...(team ? [[T('teamtilhørighet'), usersQ] as [string, typeof usersQ]] : []),
          ...(can('vaktplan.se') ? [[T('team'), teamsQ] as [string, typeof teamsQ]] : []),
        ]}
      />
      {!listKnown ? (
        shiftsQ.isError || (team && usersQ.isError) ? null : (
          <div style={{ padding: 16 }}>
            <Skeleton rows={4} />
          </div>
        )
      ) : shown.length === 0 ? (
        <Empty>
          {team && rows.length > 0 && scoped.length === 0
            ? T('Ingen vakter i dette teamet denne dagen.')
            : scoped.length === 0
              ? rota
                ? T('Ingen vakter eller registreringer denne dagen.')
                : T('Ingen registreringer denne dagen ennå.')
              : T('Ingen i dette utvalget.')}
        </Empty>
      ) : (
        <table className="mt cards oxt" data-testid="ov-list">
          <thead>
            <tr>
              <th>{T('Medarbeider')}</th>
              {rota && <th>{T('Vakt')}</th>}
              {rota && <th>{T('Planlagt')}</th>}
              <th>{T('Registrert')}</th>
              <th>{T('Status')}</th>
              <th>
                <span className="sr">{T('Handling')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const s = r.shift;
              const p = plannedText(s, r);
              const a = actualText(s);
              const left = tasksLeft(s);
              const carried = (oslo(s.start_time)?.date ?? d) < d;
              const name = s.user_id == null ? T('Ledig vakt') : fullName(s.user);
              return (
                <tr key={s.id} className={selectedId === s.id ? 'sel' : ''} data-testid="ov-row">
                  <td>
                    <span className="mwho">
                      <span className={`av ${avTone(s.user_id)}`}>
                        {s.user_id == null ? '–' : initialsOf(name)}
                      </span>
                      <span>
                        <b>{name}</b>
                        <small>{s.user_id == null ? (s.shiftType?.name ?? '') : userTeams(s.user_id)}</small>
                      </span>
                    </span>
                  </td>
                  {rota && (
                    <td className="oxshift" data-k={T('Vakt')}>
                      {vaktText(r) || <span className="oxmute">{T('Ikke planlagt')}</span>}
                    </td>
                  )}
                  {rota && (
                    <td className="oxtime" data-k={T('Planlagt')}>
                      {p ? (
                        <>
                          <span className="tnum">{p}</span>
                          <small>
                            {carried ? T('Vakt fra i går') : s.published ? T('Publisert') : T('Utkast')}
                          </small>
                        </>
                      ) : (
                        <span className="oxmute">{T('Ingen planlagt vakt')}</span>
                      )}
                    </td>
                  )}
                  <td className="oxtime" data-k={T('Registrert')}>
                    {a ? (
                      <>
                        <span className="tnum">{a}</span>
                        {showsPause(r.pauseMin, s.break_mode ?? breakMode) && (
                          <small>
                            {r.pauseMin ?? 0} {T('min registrert pause')}
                          </small>
                        )}
                      </>
                    ) : (
                      <span className="oxmute">{s.user_id == null ? '–' : T('Ikke stemplet inn')}</span>
                    )}
                  </td>
                  <td className="oxstatus" data-k={T('Status')}>
                    <Status tone={r.tone}>{T(r.label)}</Status>
                    {left != null && left > 0 && (
                      <small>{plural(left, 'aktivitet gjenstår', 'aktiviteter gjenstår')}</small>
                    )}
                  </td>
                  <td className="oxact">
                    {s.user_id != null && mayCorrect && (
                      <button
                        type="button"
                        className="mlink"
                        onClick={() => setDraft(draftOf(s))}
                        aria-label={`${T('Åpne vakten')}: ${name}`}
                      >
                        {T('Åpne vakten')}
                      </button>
                    )}
                    {s.user_id == null && can('vaktplan.endre') && (
                      <button
                        type="button"
                        className="mlink"
                        onClick={() => setDraft(draftOf(s))}
                        aria-label={T('Tildel ledig vakt {from} til {to}', {
                          from: clock(s.start_time),
                          to: clock(s.end_time),
                        })}
                      >
                        {T('Tildel')}
                      </button>
                    )}
                    <button
                      type="button"
                      className={r.review ? 'btn sm' : 'mlink'}
                      onClick={() => openRow(r)}
                      data-day-open={s.id}
                      aria-label={`${r.review ? T('Avklar') : T('Se detaljer')}: ${name}, ${T(r.label)}`}
                    >
                      {r.review && <Icon name="warn" />} {r.review ? T('Avklar') : T('Se detaljer')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {listKnown && (
        <div className="mcf">
          {rota ? (
            <span>
              {plural(planned.length, 'vakt', 'vakter')} · {plural(plannedPeople, 'person', 'personer')} ·{' '}
              {num(est.hours, 1)} {T('t planlagt')}
              {seesCost &&
                showCost &&
                (labour.ready
                  ? ` · ${kr(est.cost)} kr ${T('estimert')}`
                  : ` · ${T('Kostnad ikke tilgjengelig')}`)}
            </span>
          ) : (
            <span>{plural(scoped.length, 'registrering', 'registreringer')}</span>
          )}
          {rota && seesCost && (
            <button
              type="button"
              className="mlink"
              onClick={() => setShowCost((x) => !x)}
              aria-pressed={showCost}
            >
              {showCost ? T('Skjul kostnad') : T('Vis kostnad')}
            </button>
          )}
          <span className="sp" />
          <span className="oxmute">
            {shown.length} {rota ? T('vakter / registreringer vist') : T('registreringer vist')}
          </span>
        </div>
      )}
    </section>
  );

  // beside the team list, so who is here and whether it is enough read together (owner, 2026-10-19)
  const staffing = rota && can('vaktplan.se') && (
    <section className="mcard" id="ov-staff" aria-labelledby="ov-staff-h" data-testid="ov-staff">
      <div className="mch">
        <div>
          <h2 id="ov-staff-h" tabIndex={-1}>
            {T('Bemanning mot behov')}
          </h2>
          <span className="oxscope">{T('Hele virksomheten · alle team')}</span>
          <div className="sub">
            {!staffingReady
              ? T('Venter på oppdaterte vakter og bemanningsbehov.')
              : hasNeed
                ? gaps.length
                  ? T('Planlagt under behovet i {n} tidsrom. Tallene er personer på jobb samtidig.', {
                      n: gaps.length,
                    })
                  : T('Planen dekker behovet hele dagen.')
                : T('Behovet er ikke satt for denne dagen.')}
          </div>
        </div>
        <span className="sp" />
        {staffingReady && !hasNeed && can('innstillinger.se') && (
          <Link className="mlink" to="/innstillinger/bemanning">
            <Icon name="sliders" />
            {T('Sett behov')}
          </Link>
        )}
      </div>
      <ReadNotice
        reads={[
          [T('dagens vakter'), shiftsQ],
          [T('bemanningsbehov'), labour.query],
        ]}
      />
      {staffingKnown && gaps.length > 0 && (
        <table className="mt cards">
          <thead>
            <tr>
              <th>{T('Tidsrom')}</th>
              <th className="num">{T('Planlagt')}</th>
              <th className="num">{T('Behov')}</th>
              <th>
                <span className="sr">{T('Handling')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {gaps.map((g) => (
              <tr key={`${g.from}-${g.to}`}>
                <td className="tnum">{gapText(g)}</td>
                <td className="num" data-k={T('Planlagt')}>
                  {g.have}
                </td>
                <td className="num" data-k={T('Behov')}>
                  {g.need}
                </td>
                <td className="oxact">
                  {staffingReady && can('vaktplan.endre') ? (
                    <Link
                      className="mlink"
                      to={toVaktplan({
                        day: d,
                        newShift: { from: g.from, to: g.to },
                      })}
                      aria-label={`${T('Opprett ledig vakt')} ${gapText(g)}`}
                    >
                      <Icon name="plus" />
                      {T('Opprett vakt')}
                    </Link>
                  ) : (
                    <Link className="mlink" to={toVaktplan({ day: d })}>
                      <Icon name="calendar" />
                      {T('Åpne dagsplan')}
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {shiftsQ.data && (
        <RecordedVsPlanned
          shifts={dayShifts}
          d={d}
          now={now}
          isToday={d === today}
          openingHours={tenant?.opening_hours}
        />
      )}
      <div className="oxfree" data-testid="ov-free">
        <h3>
          {T('Finn ekstra bemanning')} {candidatesReady ? `(${free.length})` : ''}
        </h3>
        <p className="oxmute">
          {T(
            'Ikke satt opp denne dagen, ikke borte og ikke meldt utilgjengelig. Ikke oppgitt tilgjengelighet betyr ikke at personen kan.',
          )}
        </p>
        {!can('fravaer.se') ? (
          <p>{T('Du trenger tilgang til fravær for å se bemanningsforslag.')}</p>
        ) : (
          <ReadNotice
            reads={[
              [T('dagens vakter'), shiftsQ],
              [T('ansatte'), usersQ],
              [T('fravær'), absQ],
              [T('tilgjengelighet'), availQ],
              [T('bemanningsbehov'), labour.query],
            ]}
          />
        )}
        {candidatesReady && free.length > 0 && (
          <input
            className="inp"
            type="search"
            value={freeQ}
            onChange={(e) => setFreeQ(e.target.value)}
            placeholder={T('Søk etter ansatt …')}
            aria-label={T('Søk etter ansatt')}
          />
        )}
        {!candidatesReady ? null : freeList.length === 0 ? (
          <p className="oxmute">{freeQ.trim() ? T('Ingen treff.') : T('Ingen flere å foreslå.')}</p>
        ) : (
          <ul>
            {freeList.map((u) => {
              const av = availOn(availQ.data ?? [], u.id, d);
              return (
                <li key={u.id}>
                  <span className="mwho">
                    <span className={`av ${avTone(u.id)}`}>{initialsOf(fullName(u))}</span>
                    <span>
                      <b>{fullName(u)}</b>
                      <small>
                        {av?.kind === 'partly'
                          ? T('Delvis tilgjengelig {from}–{to}', {
                              from: av.from,
                              to: av.to,
                            })
                          : T('Tilgjengelighet ikke oppgitt')}
                        {u.max_hours_per_week != null ? (
                          ` · ${num(u.max_hours_per_week)} ${T('t avtalt per uke')}`
                        ) : can('ansatte.endre') ? (
                          // the agreed week reaches those who manage people only (models/user.js);
                          // for anyone else it is absent, and 37,5 would state a contract that may not be theirs
                          <>
                            {' · '}
                            <span title={T('Ikke oppgitt, standard brukt')}>
                              37,5* {T('t avtalt per uke')}
                            </span>
                          </>
                        ) : (
                          ''
                        )}
                      </small>
                    </span>
                  </span>
                  {can('vaktplan.endre') && (
                    <Link
                      className="mlink"
                      to={
                        firstGap
                          ? toVaktplan({
                              day: d,
                              newShift: {
                                from: firstGap.from,
                                to: firstGap.to,
                                userId: u.id,
                              },
                            })
                          : toVaktplan({ day: d })
                      }
                      aria-label={`${T('Sett opp')} ${fullName(u)}${firstGap ? ` ${gapText(firstGap)}` : ''}`}
                    >
                      <Icon name="sliders" />
                      {T('Sett opp')}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!freeQ.trim() && free.length > freeList.length && (
          <button type="button" className="mlink" onClick={() => setFreeAll(true)}>
            {T('Vis alle {n}', { n: free.length })} →
          </button>
        )}
      </div>
    </section>
  );

  const activities = shiftsQ.data && (rota || tasks.length > 0) && (
    <section className="mcard" aria-labelledby="ov-tasks" data-testid="ov-tasks">
      <div className="mch">
        <h2 id="ov-tasks">{T('Aktiviteter nå')}</h2>
        <span className="sub">
          {tasks.length} {T('gjenstår')}
        </span>
      </div>
      {nowTasks.length === 0 && laterTasks.length === 0 ? (
        <p className="oxpad oxmute">{T('Ingen åpne aktiviteter denne dagen.')}</p>
      ) : (
        <ul className="oxtasks">
          {(nowTasks.length ? nowTasks : []).map(({ a, sh, due }) => (
            <TaskRow
              key={a.id}
              a={a}
              sh={sh}
              overdue={due < now}
              done={doneTask === a.id}
              onDone={complete}
            />
          ))}
          {nowTasks.length === 0 && <li className="oxmute">{T('Ingen aktiviteter akkurat nå.')}</li>}
        </ul>
      )}
      {laterTasks.length > 0 && (
        <div className="mcf">
          <button
            type="button"
            className="mlink"
            aria-expanded={laterOpen}
            onClick={() => setLaterOpen((x) => !x)}
          >
            {T('Senere i dag')} · {plural(laterTasks.length, 'aktivitet', 'aktiviteter')}
          </button>
        </div>
      )}
      {laterOpen && (
        <ul className="oxtasks">
          {laterTasks.map(({ a, sh }) => (
            <TaskRow key={a.id} a={a} sh={sh} overdue={false} done={doneTask === a.id} onDone={complete} />
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <div className="oxday">
      {rota && <FirstPlanHint on="hjem" />}
      <div className="mbar">
        <PeriodControl
          value={d}
          onChange={go}
          prevLabel={T('Forrige dag')}
          nextLabel={T('Neste dag')}
          onPrev={() => go(addDays(d, -1))}
          onNext={() => go(addDays(d, 1))}
        >
          <span aria-live="polite">{dayTitle(d)}</span>
        </PeriodControl>
        <span className="sp" />
        <div className="mfresh">
          {updated && <div>{T('Oppdatert {time}', { time: updated })}</div>}
          {d !== today && (
            <Button ghost onClick={() => go(today)}>
              {T('I dag')}
            </Button>
          )}
          <button
            type="button"
            className="mlink"
            onClick={() => {
              void invalidateShifts(qc);
              for (const q of [
                usersQ,
                availQ,
                ...(can('vaktplan.se') ? [labour.query, teamsQ] : []),
                ...(can('fravaer.se') ? [absQ] : []),
              ])
                void q.refetch();
            }}
          >
            <Icon name="refresh" />
            <span className="oxrefresh-label">{T('Oppdater visning')}</span>
          </button>
        </div>
      </div>

      <div className="oxbrief" aria-label={T('Dagens status')}>
        <button
          type="button"
          onClick={() => {
            set('vis', 'avklar');
            document.getElementById('ov-team')?.focus();
          }}
          disabled={!listKnown}
        >
          <Icon name="warn" />
          <span>
            <b>
              {listKnown
                ? plural(c.review, 'vakt å avklare', 'vakter å avklare')
                : T('Henter dagens status…')}
            </b>
            <small>
              {team ? (teamName(team) ?? T('Valgt team')) : T('Alle team')} · {T('se listen')}
            </small>
          </span>
          <Icon name="chevronRight" />
        </button>
        {rota && can('vaktplan.se') && (
          <button
            type="button"
            aria-label={T('Se bemanning for hele virksomheten')}
            onClick={() => document.getElementById('ov-staff-h')?.focus()}
          >
            <Icon name="users" />
            <span>
              <b>
                {!staffingReady
                  ? T('Bemanning ikke bekreftet')
                  : !hasNeed
                    ? T('Bemanningsbehov ikke satt')
                    : gaps.length
                      ? T('{n} tidsrom under behov', { n: gaps.length })
                      : T('Planen dekker behovet')}
              </b>
              <small>{T('Hele virksomheten')}</small>
            </span>
            <Icon name="chevronRight" />
          </button>
        )}
      </div>

      <div className={`msplit${selected ? ' open' : ''}`}>
        <div className="oxmain">
          {staffing ? (
            <div className="oxpair">
              {list}
              {staffing}
            </div>
          ) : (
            list
          )}
          {rota && can('vaktplan.se') && staffingKnown && (
            <Coverage
              shifts={dayAll}
              d={d}
              need={labour.need}
              openingHours={tenant?.opening_hours}
              current={staffingReady}
            />
          )}
          {activities}
          <Explain q={T('Neste dag og kommende fravær')} className="oxnext">
            <p>
              <Link to={`/hjem?d=${tomorrow}`}>{T('Se {day}', { day: dayTitle(tomorrow) })} →</Link>
            </p>
            {can('fravaer.se') &&
              (absQ.data === undefined || absQ.isError ? (
                <ReadNotice reads={[[T('kommende fravær'), absQ]]} />
              ) : ahead.length ? (
                <ul className="oxabs">
                  {ahead.slice(0, 8).map((a) => (
                    <li key={a.id}>
                      <Link to={`/fravaer/soknader?apne=${a.id}`}>
                        <b>{a.user ? fullName(a.user) : T('Ukjent')}</b> ·{' '}
                        {typeName(a.absence_type, T('Fravær'))}
                      </Link>
                      <span className="tnum">
                        {fmtDs(oslo(a.start_date)?.date ?? a.start_date)}
                        {a.end_date !== a.start_date
                          ? ` – ${fmtDs(oslo(a.end_date)?.date ?? a.end_date)}`
                          : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="oxmute">{T('Ingen godkjent fravær de neste 14 dagene.')}</p>
              ))}
          </Explain>
          <div className="oxsec">
            <Button small onClick={exportCsv} disabled={!shown.length}>
              <Icon name="download" />
              {T('Eksporter vist liste')}
            </Button>
            {seesHistory && (
              <Explain q={T('Endringshistorikk')} className="oxhist">
                {historyQ.isError ? (
                  <ReadNotice reads={[[T('endringshistorikk'), historyQ]]} />
                ) : historyQ.isPending ? (
                  <Skeleton rows={2} />
                ) : hendelser.length ? (
                  <ol className="oxevents">
                    {hendelser.slice(0, 12).map((e) => (
                      <li key={e.id}>
                        <span className="tnum">{clock(e.at)}</span> {e.text}
                        {e.note && <small> · {e.note}</small>}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="oxmute">{T('Ingen stemplinger registrert denne dagen ennå.')}</p>
                )}
                <Link to={`/arbeidstid?periode=dag&dato=${d}`}>{T('Se alle registreringer')} →</Link>
              </Explain>
            )}
          </div>
        </div>
        {selected && (
          <DetailPanel
            restoreFocusTo={`[data-day-open="${selected.shift.id}"]`}
            title={
              selected.shift.user_id == null ? (
                T('Ledig vakt')
              ) : (
                <PersonLabel id={selected.shift.user_id} name={fullName(selected.shift.user)} />
              )
            }
            sub={`${dayTitle(oslo(selected.shift.start_time)?.date ?? d)} · ${T(selected.label)}`}
            onClose={close}
            label={T('Detaljer')}
            footer={
              <>
                {selected.shift.user_id != null &&
                  mayCorrect &&
                  (selected.review || selected.shift.punch_in_time != null) && (
                    <Button tone="pri" onClick={() => setDraft(draftOf(selected.shift))}>
                      {T('Rett stempling')}
                    </Button>
                  )}
                {selected.shift.user_id == null && can('vaktplan.endre') && (
                  <Link className="btn pri" to={toVaktplan({ day: d })}>
                    <Icon name="users" />
                    {T('Finn noen i dagsplanen')}
                  </Link>
                )}
                {rota && selected.shift.user_id != null && (
                  <Link className="btn" to={toVaktplan({ day: d })}>
                    <Icon name="calendar" />
                    {T('Åpne dagsplan')}
                  </Link>
                )}
                <Button ghost onClick={close}>
                  {T('Lukk')}
                </Button>
              </>
            }
          >
            {selected.review && <p>{T(selected.explain)}</p>}
            {rota && (
              <Fact k={T('Planlagt vakt')}>
                {selected.unplanned
                  ? T('Ingen')
                  : `${clock(selected.shift.start_time)}–${clock(selected.shift.end_time)}`}
              </Fact>
            )}
            {rota && !selected.unplanned && selected.shift.planned_lunch_start && (
              <Fact k={T('Planlagt lunsj')}>
                {`${clock(selected.shift.planned_lunch_start)} · ${selected.shift.planned_lunch_minutes ?? 0} min`}
              </Fact>
            )}
            <Fact k={T('Registrert inn')}>
              {selected.shift.punch_in_time ? clock(selected.shift.punch_in_time) : T('Ikke registrert')}
            </Fact>
            <Fact k={T('Registrert ut')}>
              {selected.shift.punch_out_time ? clock(selected.shift.punch_out_time) : T('Ikke registrert')}
            </Fact>
            <Fact k={T('Registrert pause')}>
              {selected.pauseMin == null ? '–' : `${selected.pauseMin} min`}
            </Fact>
            {selected.shift.punch_in_time && selected.shift.punch_out_time && (
              <Fact k={T('Registrert tid')}>{`${num(hours(selected.shift, null), 2)} t`}</Fact>
            )}
            {(selected.shift.taskAssignments ?? []).length > 0 && (
              <div className="oxptasks">
                <b>{T('Aktiviteter')}</b>
                <ul>
                  {(selected.shift.taskAssignments ?? []).map((a) => (
                    <li key={a.id}>
                      {a.shiftTask?.name ?? T('Aktivitet')}{' '}
                      <small>
                        {a.status === 'completed'
                          ? `${T('fullført')}${a.completed_at ? ` ${clock(a.completed_at)}` : ''}${a.completedByUser ? ` ${T('av')} ${fullName(a.completedByUser)}` : ''}`
                          : T('ikke fullført')}
                      </small>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="oxplinks">
              {selected.shift.user_id != null && can('ansatte.se') && (
                <Link to={`/ansatte/${selected.shift.user_id}`}>{T('Åpne ansattprofil')} →</Link>
              )}
              {selected.shift.user_id != null && can('timeliste.se') && (
                <Link to={`/arbeidstid?periode=dag&dato=${d}&ansatt=${selected.shift.user_id}`}>
                  {T('Se registreringer i Kontroll')} →
                </Link>
              )}
              {team > 0 && <span className="oxmute">{teamName(team)}</span>}
            </div>
          </DetailPanel>
        )}
      </div>

      <ShiftDrawer
        draft={draft}
        focus={draft?.punchIn && !draft.punchOut ? { punch: 'out' } : undefined}
        tasks={drawerShift?.taskAssignments}
        entries={
          drawerShift && drawerUser && tenant
            ? dayEntries(drawerShift, rulePerson(drawerUser, tenant), tenantRules())
            : []
        }
        types={types}
        users={users.filter((u) => u.active_status)}
        onClose={() => setDraft(null)}
        // the editor's save and its delete cross the same seam as every shift write in Vaktplan
        // (lock.ts): a double click sends one request, an unknown outcome locks the shift
        submit={(id, f) =>
          lock.op([id], T('Lagret'), async () => {
            await f();
            return undefined;
          })
        }
        locked={draft?.id != null && lock.busy(draft.id)}
        onReread={() => void lock.reread()}
        onDelete={async (id, reason) => {
          const ok = await lock.op([id], T('Vakten er slettet'), async () => {
            await w.remove.mutateAsync({ id, ...(reason ? { reason } : {}) });
            return undefined;
          });
          if (ok) {
            setDraft(null);
            close();
          }
        }}
      />
    </div>
  );
}

/** When an activity starts: its own clock, else the shift's start. */
function taskStart(a: { has_time_range?: boolean; start_time?: string | null }, sh: ApiShift): number {
  if (a.has_time_range && a.start_time) {
    if (/^\d{2}:\d{2}/.test(a.start_time)) {
      const day = oslo(sh.start_time)?.date;
      return day ? Date.parse(osloISO(day, a.start_time.slice(0, 5))) : Date.parse(sh.start_time);
    }
    const t = Date.parse(a.start_time);
    if (Number.isFinite(t)) return t;
  }
  return Date.parse(sh.start_time);
}

function TaskRow({
  a,
  sh,
  overdue,
  done,
  onDone,
}: {
  a: NonNullable<ApiShift['taskAssignments']>[number];
  sh: ApiShift;
  overdue: boolean;
  done: boolean;
  onDone: (id: number) => void;
}) {
  const when =
    a.has_time_range && a.start_time
      ? `${hhmm(a.start_time)}${a.end_time ? `–${hhmm(a.end_time)}` : ''}`
      : `${clock(sh.start_time)}–${clock(sh.end_time)}`;
  return (
    <li>
      <span>
        <b>{a.shiftTask?.name ?? T('Aktivitet')}</b>
        <small>
          {sh.user ? fullName(sh.user) : T('Ledig vakt')} · {when}
          {overdue ? ` · ${T('ikke fullført')}` : ''}
        </small>
      </span>
      <Button small onClick={() => onDone(a.id)} disabled={done}>
        {done ? T('Fullfører…') : T('Fullfør')}
      </Button>
    </li>
  );
}

/** Planned people against recorded people per hour, the recorded side only up to now (Hjem's comparison, kept). */
function RecordedVsPlanned({
  shifts,
  d,
  now,
  isToday,
  openingHours,
}: {
  shifts: ApiShift[];
  d: string;
  now: number;
  isToday: boolean;
  openingHours: Parameters<typeof span>[2];
}) {
  const [from, to] = span(shifts, d, openingHours);
  const planned = perHour(shifts, d).filter((b) => b.hour >= from && b.hour < to);
  const startOf = (h: number) => Date.parse(osloISO(d, `${String(h).padStart(2, '0')}:00`));
  const recorded = (h: number) => {
    const a = startOf(h);
    const b = a + 3_600_000;
    return new Set(
      shifts
        .filter((s) => s.user_id != null && s.punch_in_time)
        .filter((s) => {
          const i = Date.parse(s.punch_in_time as string);
          const o = s.punch_out_time ? Date.parse(s.punch_out_time) : now;
          return i < b && o > a;
        })
        .map((s) => s.user_id),
    ).size;
  };
  return (
    <details className="mexp oxrec">
      <summary>{T('Planlagt mot registrert, time for time')}</summary>
      <div className="mexb">
        <p>
          {T(
            'Planlagt teller personer med vakt i timen. Registrert teller personer stemplet inn i timen, og bare fram til nå.',
          )}
        </p>
        <table className="mt oxhours">
          <caption className="sr">{T('Planlagt mot registrert')}</caption>
          <thead>
            <tr>
              <th>{T('Time')}</th>
              <th className="num">{T('Planlagt')}</th>
              <th className="num">{T('Registrert')}</th>
            </tr>
          </thead>
          <tbody>
            {planned.map((b, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the autumn DST day has two 02 hours
              <tr key={i}>
                <td className="tnum">{String(b.hour).padStart(2, '0')}:00</td>
                <td className="num">{b.count}</td>
                <td className="num">{isToday && startOf(b.hour) > now ? '–' : recorded(b.hour)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * Planned people per hour as bars against the need as a dashed line: the old Dagen's Bemanning
 * chart, back unfolded because managers read the day from it (issue #94). The gap table under
 * "Se bemanningsbehov" counts people at the same time inside the hour; this counts people with a
 * shift in the hour, which is what the bar can show.
 */
function Coverage({
  shifts,
  d,
  need,
  openingHours,
  current,
}: {
  shifts: ApiShift[];
  d: string;
  need: NeedProfile;
  openingHours: Parameters<typeof span>[2];
  current: boolean;
}) {
  const can = useCan();
  const [from, to] = span(shifts, d, openingHours);
  const bars = perHour(shifts, d)
    .filter((b) => b.hour >= from && b.hour < to)
    .map((b) => ({ ...b, need: needAt(need, dow(d), b.hour) }));
  const hasNeed = bars.some((b) => b.need > 0);
  const short = bars.filter((b) => b.count < b.need).length;
  const maxY = Math.max(8, ...bars.map((b) => Math.max(b.count, b.need)));
  const step = 36;
  const W = 44 + bars.length * step + 12;
  const base = 118;
  const sc = 100 / maxY;
  const ticks = [0, 2, 4, 6, 8].filter((v) => v <= maxY).concat(maxY > 8 ? [maxY] : []);
  const hh = (h: number) => String(h).padStart(2, '0');
  return (
    <section className="mcard" aria-labelledby="ov-cover" data-testid="ov-cover">
      <div className="mch">
        <div>
          <h2 id="ov-cover">{T('Bemanning')}</h2>
          <span className="oxscope">{T('Hele virksomheten · alle team')}</span>
        </div>
        <span className="sp" />
        {!current ? (
          <span aria-live="polite">{T('Sist hentede tall · ikke oppdatert')}</span>
        ) : !hasNeed ? (
          <span className="dneed">
            {T('Behov ikke satt')} ·{' '}
            {can('innstillinger.se') && (
              <Link to="/innstillinger/bemanning">
                <Icon name="sliders" />
                {T('Sett behov')}
              </Link>
            )}
          </span>
        ) : short ? (
          <Chip tone="warn" data-testid="ov-cover-chip">
            {short} {T('timer under behov')}
          </Chip>
        ) : (
          <Chip tone="ok" data-testid="ov-cover-chip">
            {T('Dekket')}
          </Chip>
        )}
      </div>
      <div className="oxpad">
        <div className="dylab">{T('Antall ansatte')}</div>
        <div className="dchart">
          <svg
            className="svg"
            viewBox={`0 0 ${W} 148`}
            style={{ minWidth: W * 0.85 }}
            role="img"
            aria-label={T('Oppsatt bemanning mot behov')}
            data-testid="coverage"
          >
            {ticks.map((v) => (
              <g key={v}>
                <line
                  x1={40}
                  y1={base - v * sc}
                  x2={W - 8}
                  y2={base - v * sc}
                  style={{ stroke: 'var(--hair)' }}
                />
                <text
                  x={34}
                  y={base - v * sc + 3}
                  textAnchor="end"
                  fontSize={10}
                  fontWeight={600}
                  style={{ fill: 'var(--ink3)' }}
                >
                  {v}
                </text>
              </g>
            ))}
            {bars.map((b, i) => {
              const x = 44 + i * step;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: the autumn DST day has two 02 hours
                <g key={i}>
                  <rect
                    x={x}
                    y={base - b.count * sc}
                    width={26}
                    height={b.count * sc}
                    rx={2}
                    style={{
                      fill: b.count < b.need ? 'var(--dgr-l)' : 'var(--act)',
                    }}
                  >
                    <title>{`${hh(b.hour)}:00 · ${b.count}`}</title>
                  </rect>
                  {b.hour % 2 === 0 && (
                    <text
                      x={x + 13}
                      y={133}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight={600}
                      style={{ fill: 'var(--ink3)' }}
                    >
                      {hh(b.hour)}
                    </text>
                  )}
                </g>
              );
            })}
            {hasNeed && (
              <polyline
                points={bars.map((b, i) => `${44 + i * step + 13},${base - b.need * sc}`).join(' ')}
                style={{ fill: 'none', stroke: 'var(--ink1)' }}
                strokeWidth={2}
                strokeDasharray="5 3"
              />
            )}
          </svg>
        </div>
        <div className="legend2">
          <span>
            <i style={{ background: 'var(--act)' }} />
            {T('Oppsatt')}
          </span>
          {hasNeed && (
            <span>
              <i style={{ background: 'var(--dgr-l)' }} />
              {T('Under behov')}
            </span>
          )}
          <span className={hasNeed ? '' : 'mute'}>
            <i className="dash" />
            {hasNeed ? T('Behov') : T('Behov (ikke satt)')}
          </span>
        </div>
      </div>
    </section>
  );
}
